import { useState, useMemo, useRef, useEffect } from "react";
import { some, toLower } from "lodash";
import {
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Box,
  Chip,
  LinearProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";

import { useRuns } from "contexts/RunsContext";
import { useSimulations } from "contexts/SimulationsContext";
import Run from "./Run";
import SelectedRunBanner from "./SelectedRunBanner";
import NoData from "../ui/NoData";
import { useNavigate } from "react-router-dom";
import usePage from "hooks/usePage";
import SortIcon from "@mui/icons-material/Sort";
import _orderBy from "lodash/orderBy";

export default function RunsList() {
  const { runs, fetchMoreRuns, hasMoreRuns, isLoadingRuns } = useRuns();
  const { isCreatingSimulation } = useSimulations();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);

  const toggleFilter = (key) => {
    setActiveFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const navigate = useNavigate();
  const { runId, page, isSharePage } = usePage();
  const scrollableDivRef = useRef(null);
  const sortButtonRef = useRef(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const handleSortMenuOpen = () => {
    setSortMenuOpen(true);
  };

  const handleSortMenuClose = () => {
    setSortMenuOpen(false);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setSortMenuOpen(false);
  };

  useEffect(() => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }
  }, [page]);

  const handleRunClick = (e, runLink) => {
    e.preventDefault();
    if (isCreatingSimulation) {
      e.stopPropagation();
      return;
    }
    navigate(runLink);
  };

  const filteredRuns = useMemo(() => {
    let filtered = runs ? [...runs] : [];

    if (activeFilters.includes("successful")) {
      filtered = filtered.filter((run) => run.status === "completed");
    }
    if (activeFilters.includes("favorites")) {
      filtered = filtered.filter((run) => run.favorited);
    }
    if (activeFilters.includes("viewer")) {
      filtered = filtered.filter((run) => run.shared);
    }

    if (searchTerm.length > 1) {
      filtered = filtered.filter((run) =>
        some(
          run,
          (value) =>
            value && toLower(String(value)).includes(toLower(searchTerm))
        )
      );
    }

    return filtered;
  }, [runs, searchTerm, activeFilters]);

  const sortedRuns = useMemo(() => {
    const order = sortBy === "created_at" ? "desc" : "asc";
    if (sortBy === "alphabetical") {
      return _orderBy(filteredRuns, [(run) => run.name.toLowerCase()], [order]);
    }
    return _orderBy(filteredRuns, ["created_at"], [order]);
  }, [filteredRuns, sortBy]);

  const selectedRun = useMemo(
    () => runs.find((r) => r.id === runId || r.hash === runId),
    [runs, runId]
  );

  const scrollToSelectedRun = () => {
    if (!selectedRun || !scrollableDivRef.current) return;
    const el = scrollableDivRef.current.querySelector(
      `[data-run-id="${selectedRun.id}"]`
    );
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <CardContent
      sx={{
        width: "100%",
        p: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        "&:last-child": {
          paddingBottom: 0,
        },
      }}
    >
      {!isSharePage && (
        <Box sx={{ px: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {[
              { key: "successful", label: "Successful" },
              { key: "favorites", label: "Favorites" },
              { key: "viewer", label: "Viewer" },
            ].map(({ key, label }) => {
              const active = activeFilters.includes(key);
              return (
                <Chip
                  key={key}
                  label={label}
                  clickable
                  onClick={() => toggleFilter(key)}
                  style={{ borderRadius: 8 }}
                  sx={{
                    borderRadius: 8,
                    border: "none",
                    height: 32,
                    px: 0.5,
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    bgcolor: active ? "primary.main" : "action.hover",
                    color: active ? "primary.contrastText" : "text.secondary",
                    "&:hover": {
                      bgcolor: active ? "primary.dark" : "action.selected",
                    },
                  }}
                />
              );
            })}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Filter"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => setSearchTerm("")}
                      edge="end"
                      size="small"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: "100%" }}
            />
            <Button
              ref={sortButtonRef}
              variant={sortMenuOpen ? "contained" : "outlined"}
              size="small"
              sx={{ minWidth: "16px", alignSelf: "stretch" }}
              onClick={handleSortMenuOpen}
            >
              <SortIcon />
            </Button>
            <Menu
              anchorEl={sortButtonRef.current}
              open={sortMenuOpen}
              onClose={handleSortMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                selected={sortBy === "created_at"}
                onClick={() => handleSortChange("created_at")}
              >
                Created At
              </MenuItem>
              <MenuItem
                selected={sortBy === "alphabetical"}
                onClick={() => handleSortChange("alphabetical")}
              >
                Alphabetical
              </MenuItem>
            </Menu>
          </Box>

          <SelectedRunBanner run={selectedRun} onClick={scrollToSelectedRun} />
        </Box>
      )}

      {/* use `visibility` instead of `display` to avoid layout shift */}
      <Box
        sx={{
          width: "100%",
          visibility: isLoadingRuns ? "visible" : "hidden",
          position: "relative",
          height: "4px",
        }}
      >
        <LinearProgress sx={{ position: "absolute", left: 0, right: 0 }} />
      </Box>

      {!isLoadingRuns && sortedRuns.length === 0 ? (
        <NoData message="No Runs" />
      ) : null}

      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          flex: "1",
          margin: "10px 0",
          width: "100%",
          position: "relative",
        }}
        className="xyloplan-scrollbar"
        id="scrollableDiv"
        ref={scrollableDivRef}
      >
        <InfiniteScroll
          dataLength={sortedRuns.length}
          next={fetchMoreRuns}
          hasMore={hasMoreRuns}
          scrollableTarget="scrollableDiv"
        >
          {sortedRuns.map((run) => (
            <Run
              key={run.id}
              run={run}
              isCurrentRun={runId === run.id || runId === run.hash}
              handleRunClick={handleRunClick}
            />
          ))}
        </InfiniteScroll>
      </Box>
    </CardContent>
  );
}
